import { defineStore } from 'pinia';
import { useAuthStore } from '../../../store/shared/auth-store';
import { api } from '../../../plugins/axios';
import Conversation from '../entities/chats/conversation';
import Message from '../entities/chats/message';

import { db } from '@/firebase';
import {
	collection,
	query,
	orderBy,
	limit,
	startAfter,
	getDocs,
	getDoc,
	getCountFromServer,
	onSnapshot,
	doc,
} from 'firebase/firestore';
import { useTableStore } from '../../../store/shared/table-store';

export const useConversationStore = ({ channelType, conversationId }) =>
	defineStore(`conversationStore-${channelType}-${conversationId}`, {
		state: () => ({
			messages: [],
			messagesUnsubscribeFunction: undefined,
			conversation: undefined,
			conversationLoading: false,
			selectedMessages: [],
			errorFetchingConversation: false,
			errorFetchingMessages: true,
		}),
		getters: {
			lastMessageTimestamp(state) {
				if (!state.conversation) return '';
				return state.conversation.timestamp;
			},
		},
		actions: {
			async fetchConversation() {
				// If we already have the conversation and we listen to changes then return;
				if (this.conversation) return;

				this.errorFetchingConversation = false;

				try {
					this.conversationLoading = true;
					const authStore = useAuthStore();
					const tenantId = authStore.tenantId;

					const conversationRef = doc(
						db,
						`tenants/${tenantId}/channels/${channelType}/conversations`,
						conversationId,
					);

					const conversation = await getDoc(conversationRef);
					this.conversation = new Conversation(conversation.data());
					this.listenForConversationChanges();
				} catch (err) {
					this.errorFetchingConversation = true;
					console.error('[WhatsApp V2] error in fetchConversation', err);
				} finally {
					this.conversationLoading = false;
				}
			},
			async fetchMessages({ per_page }) {
				this.errorFetchingMessages = false;

				const authStore = useAuthStore();
				const tenantId = authStore.tenantId;
				try {
					// console.time('[WhatsApp V2] fetchMessages');
					const conversationsCollection = collection(
						db,
						`tenants/${tenantId}/channels/${channelType}/conversations/${conversationId}/messages`,
					);
					const queryData = [conversationsCollection];
					queryData.push(orderBy('timestamp', 'desc'));

					const total = (await getCountFromServer(query(...queryData))).data().count;

					const getCounter = per_page;

					const docsToListenCount = this.messages.length + getCounter;
					const snapshotQuery = query(...queryData, limit(docsToListenCount)); // listen to first messages.length items

					if (this.messagesUnsubscribeFunction) {
						this.messagesUnsubscribeFunction();
						this.messagesUnsubscribeFunction = undefined;
					}

					queryData.push(limit(getCounter));
					if (this.messages.length) {
						// queryData.push(startAfter(this.messages[this.messages.length - 1]));
						queryData.push(startAfter(this.messages[0]));
					}

					const res = await getDocs(query(...queryData));
					// console.timeEnd('[WhatsApp V2] fetchMessages');
					const data = res.docs.map((doc) => {
						const message = new Message(doc.data());
						return message;
					});
					this.messages.unshift(...res.docs.reverse());

					this.listenForChanges(snapshotQuery);

					return {
						data,
						totalRecords: total,
					};
				} catch (error) {
					this.errorFetchingMessages = true;
					console.error(
						'[WhatsApp V2] error in Fetch Message of Conversation',
						channelType,
						conversationId,
						error,
					);
				}
			},
			async syncTotalRecords() {
				const authStore = useAuthStore();
				const tenantId = authStore.tenantId;

				const conversationTableStore = useTableStore({
					tableName: `messagesList-${channelType}-${conversationId}`,
					fetchDataFunction: this.fetchMessages,
				});

				const conversationsCollection = collection(
					db,
					`tenants/${tenantId}/channels/${channelType}/conversations/${conversationId}/messages`,
				);
				const queryData = [conversationsCollection];
				queryData.push(orderBy('timestamp', 'desc'));

				const total = (await getCountFromServer(query(...queryData))).data().count;

				conversationTableStore.totalRecords = total;
			},
			listenForChanges(query) {
				const conversationTableStore = useTableStore({
					tableName: `messagesList-${channelType}-${conversationId}`,
					fetchDataFunction: this.fetchMessages,
				});
				try {
					const unsubscribe = onSnapshot(query, (querySnapshot) => {
						if (querySnapshot.metadata.fromCache) return;

						const docs = querySnapshot.docs;
						// console.log(
						// 	'[WhatsApp V2] New Snapshot From Conversation Messages',
						// 	querySnapshot,
						// 	docs,
						// );
						const data = docs.map((doc) => {
							const message = new Message(doc.data());
							return message;
						});
						data.forEach((message) => {
							const lastMessage = conversationTableStore.data.find(
								(msg) => msg.message_id === message.message_id,
							);
							if (lastMessage && lastMessage.repliedMessageData) {
								message.repliedMessageData = lastMessage.repliedMessageData;
							}
						});
						conversationTableStore.data = data.reverse();
						this.messages = docs.reverse();
						this.syncTotalRecords();
					});
					this.messagesUnsubscribeFunction = unsubscribe;
				} catch (error) {
					console.error(
						'[WhatsApp V2] error in listenForChanges of Conversation Messages',
						error,
					);
				}
			},
			stopListening() {
				if (this.messagesUnsubscribeFunction) {
					this.messagesUnsubscribeFunction();
				}
			},
			listenForConversationChanges() {
				const authStore = useAuthStore();
				const tenantId = authStore.tenantId;
				const conversationRef = doc(
					db,
					`tenants/${tenantId}/channels/${channelType}/conversations`,
					conversationId,
				);
				const unsubscribe = onSnapshot(conversationRef, (querySnapshot) => {
					if (querySnapshot.metadata.fromCache) return;
					this.conversation = new Conversation(querySnapshot.data());
				});

				this.conversationUnsubscribeFunction = unsubscribe;
			},
			stopListeningConversationChanges() {
				if (this.conversationUnsubscribeFunction) {
					this.conversationUnsubscribeFunction();
				}
			},
		},
	})();

